import type { RadarEntry } from '@/types/radar';

/**
 * Modal component to display detailed information about a radar entry
 */
export class EntryDetail {
  private modal!: HTMLElement;
  private overlay: HTMLElement;

  constructor() {
    this.createModal();
    this.overlay = this.createOverlay();
  }

  /**
   * Show the modal with entry details
   */
  show(entry: RadarEntry): void {
    this.renderContent(entry);
    this.overlay.classList.add('active');
    this.modal.classList.add('active');
    document.body.style.overflow = 'hidden';
  }

  /**
   * Hide the modal
   */
  hide(): void {
    this.overlay.classList.remove('active');
    this.modal.classList.remove('active');
    document.body.style.overflow = '';
  }

  /**
   * Create modal element
   */
  private createModal(): void {
    this.modal = document.createElement('div');
    this.modal.className = 'entry-detail-modal';
    this.modal.innerHTML = `
      <div class="modal-header">
        <h2 class="modal-title"></h2>
        <button class="modal-close" aria-label="Close">&times;</button>
      </div>
      <div class="modal-body"></div>
    `;

    const closeBtn = this.modal.querySelector('.modal-close');
    closeBtn?.addEventListener('click', () => this.hide());

    document.body.appendChild(this.modal);
  }

  /**
   * Create overlay element
   */
  private createOverlay(): HTMLElement {
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.addEventListener('click', () => this.hide());
    document.body.appendChild(overlay);
    return overlay;
  }

  /**
   * Render entry content
   */
  private renderContent(entry: RadarEntry): void {
    const title = this.modal.querySelector('.modal-title');
    const body = this.modal.querySelector('.modal-body');

    if (title) {
      title.textContent = entry.title;
    }

    if (body) {
      let changeText = '';
      if (entry.change.status === 'new') {
        changeText = '<span class="badge badge-new">New</span>';
      } else if (entry.change.status === 'moved') {
        const arrow = entry.change.direction === 'inward' ? '↑' : '↓';
        changeText = `<span class="badge badge-moved">Moved ${arrow} from ${entry.change.previousRing}</span>`;
      }

      body.innerHTML = `
        <div class="entry-meta">
          <span class="entry-ring">${entry.ring}</span>
          <span class="entry-quadrant">${entry.quadrant.replace('-', ' & ')}</span>
          ${changeText}
        </div>

        ${entry.tags.length > 0 ? `
          <div class="entry-tags">
            ${entry.tags.map((tag) => `<span class="tag">${tag}</span>`).join('')}
          </div>
        ` : ''}

        <div class="entry-description">
          ${entry.description}
        </div>

        ${entry.url ? `
          <div class="entry-link">
            <a href="${entry.url}" target="_blank" rel="noopener noreferrer">
              Learn more →
            </a>
          </div>
        ` : ''}

        ${entry.gitHash ? `
          <div class="entry-git-info">
            <div class="git-info-title">Last Updated</div>
            <div class="git-info-content">
              <span class="git-hash" title="Git commit hash">${entry.gitHash}</span>
              ${entry.gitAuthor ? `<span class="git-author">by ${entry.gitAuthor}</span>` : ''}
              ${entry.gitTimestamp ? `<span class="git-timestamp">${this.formatDate(entry.gitTimestamp)}</span>` : ''}
            </div>
          </div>
        ` : ''}
      `;
    }
  }

  /**
   * Format date for display
   */
  private formatDate(date: Date): string {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'today';
    } else if (diffDays === 1) {
      return 'yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} month${months > 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    }
  }

  /**
   * Destroy the modal
   */
  destroy(): void {
    this.modal.remove();
    this.overlay.remove();
  }
}
